// TawPreviewDrawer — the read-only preview surface for a TAW (team agent
// workspace) transaction, opened from the TW scope-picked list (D-20). Mirrors
// src/components/features/financialRecords/v2/Drawer.js's shell rather than
// inventing a new one, and reuses that same stylesheet so the themed scrim and
// panel surface resolve without adding any CSS. Right-side slide-over on
// desktop, bottom sheet expanding to full screen on mobile (D-22). Hosts
// ParentChildTransactionView (now chrome-less) as its body — no fetch, no
// loading state, since the row is already in memory from the scope-picked
// list. Strictly a viewer: no editable control, no TransactionFormV2 (D-14,
// T-41-07-02).

import React from 'react';
import { createPortal } from 'react-dom';
import '../../financialRecords/v2/drawer.css';
import ParentChildTransactionView, { tawStatusBadgeClass } from '../ParentChildTransactionView';

function CloseIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

function ExpandIcon({ expanded }) {
    return expanded ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 14h6m0 0v6m0-6l-7 7m17-11h-6m0 0V4m0 6l7-7" />
        </svg>
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l6 6m10-6v4m0-4h-4m4 0l-6 6M4 16v4m0 0h4m-4 0l6-6m10 6l-6-6m6 6v-4m0 4h-4" />
        </svg>
    );
}

function TawPreviewDrawer({ open, transaction, expModuleSettings, leaderPlanState = null, teamSplitRecords = [], onOpenInWorkspace, onClose }) {
    const [expanded, setExpanded] = React.useState(false);

    React.useEffect(() => {
        if (!open) return undefined;
        setExpanded(false);
        const onKey = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;
    if (!transaction) return null;

    const workspaceName = transaction._workspaceName || 'Team workspace';
    const addressLine = transaction.propertyStreet || transaction.propertyAddress?.split('\n')[0] || 'Transaction';
    const statusClass = tawStatusBadgeClass(transaction.status);

    return createPortal(
        <div className="v2-scope fixed inset-0 z-[1000] flex items-end justify-center md:items-stretch md:justify-end">
            <div
                className="v2-drawer-backdrop absolute inset-0"
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={addressLine}
                className={`v2-drawer-panel relative flex w-full flex-col rounded-t-2xl md:h-full md:max-w-xl md:rounded-none ${expanded ? 'h-full rounded-none' : 'h-[92dvh]'}`}
                style={{ borderLeft: '1px solid var(--border-color)' }}
            >
                <button
                    type="button"
                    onClick={() => setExpanded((prev) => !prev)}
                    aria-label={expanded ? 'Collapse preview' : 'Expand preview to full screen'}
                    aria-expanded={expanded}
                    className="flex h-6 w-full flex-shrink-0 items-center justify-center text-[color:var(--text-faint)] md:hidden"
                >
                    <ExpandIcon expanded={expanded} />
                </button>
                {/* Two rows: the name and address get the full width, so a long
                    workspace name in the Open button can never squeeze them. */}
                <div
                    className="flex flex-shrink-0 flex-col gap-3 px-5 py-4 md:px-6"
                    style={{ borderBottom: '1px solid var(--border-color)' }}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-[color:var(--text-muted)]">{workspaceName}</p>
                            <h2 className="truncate text-base font-semibold text-[color:var(--text-primary)]">{addressLine}</h2>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)]"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex flex-shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass}`}>
                            {transaction.status}
                        </span>
                        <span className="text-xs italic text-[color:var(--text-faint)]">Read-only view</span>
                        {onOpenInWorkspace && (
                            <button
                                type="button"
                                onClick={onOpenInWorkspace}
                                className="ml-auto inline-flex max-w-full items-center gap-1.5 rounded-md bg-[color:var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-[color:var(--surface-primary)] shadow-sm hover:bg-[color:var(--action-link-color)]"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                <span className="truncate">Open in {workspaceName}</span>
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6">
                    <ParentChildTransactionView
                        transaction={transaction}
                        expModuleSettings={expModuleSettings}
                        leaderPlanState={leaderPlanState}
                        teamSplitRecords={teamSplitRecords}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
}

export default TawPreviewDrawer;
